
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  getCommissionOffers, 
  getCommissionOfferById, 
  createCommissionOffer, 
  updateCommissionOffer, 
  deleteCommissionOffer 
} from "@/services/commission.service";
import { CommissionOffer } from "@/types/commission.types";
import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";

// Fetch all commission offers with pagination
export const useCommissionOffers = (initialPage = 1) => {
  const [page, setPage] = useState(initialPage);
  
  const query = useQuery({
    queryKey: ['commission-offers', page],
    queryFn: () => getCommissionOffers(),
    placeholderData: keepPreviousData,
    retry: 1
  });
  
  return {
    ...query,
    page,
    setPage,
    offers: query.data || [],
    totalCount: query.data ? query.data.length : 0
  };
};

// Fetch a single commission offer by ID
export const useCommissionOffer = (id: string) => {
  return useQuery({
    queryKey: ['commission-offer', id],
    queryFn: () => getCommissionOfferById(id),
    enabled: !!id,
    retry: 1
  });
};

// Create a commission offer
export const useCreateCommissionOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (offer: Omit<CommissionOffer, 'id' | 'created_at' | 'updated_at' | 'artist'>) => {
      return createCommissionOffer(offer);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['commission-offers'] });
      if (data && data.artist_id) {
        queryClient.invalidateQueries({ queryKey: ['artist-commission-offers', data.artist_id] });
      }
    }
  });
};

// Update a commission offer
export const useUpdateCommissionOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, offer }: { id: string, offer: Partial<CommissionOffer> }) => 
      updateCommissionOffer(id, offer),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['commission-offer', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['commission-offers'] });
      if (data && data.artist_id) {
        queryClient.invalidateQueries({ queryKey: ['artist-commission-offers', data.artist_id] });
      }
    }
  });
};

// Delete a commission offer
export const useDeleteCommissionOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => deleteCommissionOffer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-offers'] });
    }
  });
};
